/**
 * Pixel Tracking Helper - NAVEGA
 * Centralized tracking for Meta Pixel and Google Ads
 * 
 * Meta Pixel ID: 702871312609852
 * Google Ads Conversion ID: AW-4390413349
 */

// Check if we're in browser environment
const isBrowser = typeof window !== 'undefined';

/**
 * Track Meta Pixel Event
 * @param {string} eventName - Standard or custom event name
 * @param {object} params - Event parameters
 */
export const trackMetaEvent = (eventName, params = {}) => {
  if (!isBrowser) return;
  
  try {
    if (typeof window.fbq === 'function') {
      window.fbq('track', eventName, params);
      console.log(`[Meta Pixel] Event tracked: ${eventName}`, params);
    }
  } catch (error) {
    console.warn('[Meta Pixel] Error tracking event:', error);
  }
};

/**
 * Track Google Ads Conversion Event
 * @param {string} conversionLabel - Conversion label from Google Ads
 * @param {object} params - Event parameters (value, currency, etc.)
 */
export const trackGoogleAdsConversion = (conversionLabel, params = {}) => {
  if (!isBrowser) return;
  
  try {
    if (typeof window.gtag === 'function') {
      window.gtag('event', 'conversion', {
        send_to: `AW-4390413349/${conversionLabel}`,
        ...params
      });
      console.log(`[Google Ads] Conversion tracked: ${conversionLabel}`, params);
    }
  } catch (error) {
    console.warn('[Google Ads] Error tracking conversion:', error);
  }
};

/**
 * Track Google Analytics Event
 * @param {string} eventName - Event name
 * @param {object} params - Event parameters
 */
export const trackGAEvent = (eventName, params = {}) => {
  if (!isBrowser) return;
  
  try {
    if (typeof window.gtag === 'function') {
      window.gtag('event', eventName, params);
      console.log(`[GA4] Event tracked: ${eventName}`, params);
    }
  } catch (error) {
    console.warn('[GA4] Error tracking event:', error);
  }
};

// ============================================
// STANDARD EVENTS
// ============================================

/**
 * Track Page View
 * Called automatically, but can be called manually for SPA navigation
 */
export const trackPageView = (url = null) => {
  if (!isBrowser) return;
  
  const pageUrl = url || window.location.href;
  
  // Meta Pixel
  trackMetaEvent('PageView');
  
  // Google Analytics (automatic with gtag config)
  if (typeof window.gtag === 'function') {
    window.gtag('config', 'G-YXCJMFLZ75', {
      page_path: pageUrl
    });
  }
};

/**
 * Track Complete Registration
 * Called when a user completes signup
 * @param {object} params - { method: 'email' | 'google' | 'facebook' }
 */
export const trackCompleteRegistration = (params = {}) => {
  // Meta Pixel - CompleteRegistration
  trackMetaEvent('CompleteRegistration', {
    content_name: 'User Registration',
    status: 'complete',
    ...params
  });
  
  // Google Analytics
  trackGAEvent('sign_up', {
    method: params.method || 'email'
  });
  
  // Google Ads Conversion (add your conversion label)
  // trackGoogleAdsConversion('YOUR_REGISTRATION_LABEL');
};

/**
 * Track Login
 * Called when a user logs in
 * @param {object} params - { method: 'email' | 'google' | 'facebook' }
 */
export const trackLogin = (params = {}) => {
  // Google Analytics
  trackGAEvent('login', {
    method: params.method || 'email'
  });
};

/**
 * Track View Content (Listing Detail)
 * Called when a user views a boat listing
 * @param {object} params - { listingId, title, value, currency }
 */
export const trackViewContent = (params = {}) => {
  const { listingId, title, value, currency = 'USD' } = params;
  
  // Meta Pixel - ViewContent
  trackMetaEvent('ViewContent', {
    content_type: 'product',
    content_ids: [listingId],
    content_name: title,
    value: value,
    currency: currency
  });
  
  // Google Analytics
  trackGAEvent('view_item', {
    currency: currency,
    value: value,
    items: [{
      item_id: listingId,
      item_name: title
    }]
  });
};

/**
 * Track Search
 * Called when a user performs a search
 * @param {object} params - { searchQuery, location, dates }
 */
export const trackSearch = (params = {}) => {
  const { searchQuery, location, dates } = params;
  
  // Meta Pixel - Search
  trackMetaEvent('Search', {
    search_string: searchQuery || location,
    content_category: 'boats'
  });
  
  // Google Analytics
  trackGAEvent('search', {
    search_term: searchQuery || location
  });
};

/**
 * Track Initiate Checkout (Booking Started)
 * Called when a user starts the booking process
 * @param {object} params - { listingId, title, value, currency }
 */
export const trackInitiateCheckout = (params = {}) => {
  const { listingId, title, value, currency = 'USD' } = params;
  
  // Meta Pixel - InitiateCheckout
  trackMetaEvent('InitiateCheckout', {
    content_type: 'product',
    content_ids: [listingId],
    content_name: title,
    value: value,
    currency: currency,
    num_items: 1
  });
  
  // Google Analytics
  trackGAEvent('begin_checkout', {
    currency: currency,
    value: value,
    items: [{
      item_id: listingId,
      item_name: title
    }]
  });
};

/**
 * Track Purchase (Booking Completed)
 * Called when a booking payment is successful
 * @param {object} params - { reservationId, listingId, title, value, currency }
 */
export const trackPurchase = (params = {}) => {
  const { reservationId, listingId, title, value, currency = 'USD' } = params;
  
  // Meta Pixel - Purchase
  trackMetaEvent('Purchase', {
    content_type: 'product',
    content_ids: [listingId],
    content_name: title,
    value: value,
    currency: currency,
    num_items: 1
  });
  
  // Google Analytics
  trackGAEvent('purchase', {
    transaction_id: reservationId,
    currency: currency,
    value: value,
    items: [{
      item_id: listingId,
      item_name: title
    }]
  });
  
  // Google Ads Conversion (add your conversion label for purchases)
  // trackGoogleAdsConversion('YOUR_PURCHASE_LABEL', { value, currency });
};

/**
 * Track Contact (Message to Host)
 * Called when a user sends a message to a host
 * @param {object} params - { listingId, hostId }
 */
export const trackContact = (params = {}) => {
  const { listingId, hostId } = params;
  
  // Meta Pixel - Contact
  trackMetaEvent('Contact', {
    content_type: 'product',
    content_ids: [listingId]
  });
  
  // Google Analytics
  trackGAEvent('generate_lead', {
    currency: 'USD',
    value: 0
  });
};

/**
 * Track Lead (Contact Form / Inquiry)
 * Called when a user submits a contact or inquiry form
 * @param {object} params - { source, listingId }
 */
export const trackLead = (params = {}) => {
  const { source, listingId } = params;
  
  // Meta Pixel - Lead
  trackMetaEvent('Lead', {
    content_name: source || 'inquiry',
    content_category: 'boats',
    content_ids: listingId ? [listingId] : []
  });
  
  // Google Analytics
  trackGAEvent('generate_lead', {
    event_category: 'engagement',
    event_label: source
  });
};

/**
 * Track Subscribe (Featured Listing Payment)
 * Called when a host pays for featured listing
 * @param {object} params - { listingId, value, currency, plan }
 */
export const trackSubscribe = (params = {}) => {
  const { listingId, value, currency = 'USD', plan } = params;
  
  // Meta Pixel - Subscribe
  trackMetaEvent('Subscribe', {
    value: value,
    currency: currency,
    predicted_ltv: value,
    content_name: plan || 'Featured Listing'
  });
  
  // Google Analytics
  trackGAEvent('purchase', {
    transaction_id: `featured_${listingId}_${Date.now()}`,
    currency: currency,
    value: value,
    items: [{
      item_id: 'featured_listing',
      item_name: plan || 'Featured Listing',
      price: value
    }]
  });
  
  // Google Ads Conversion (add your conversion label for subscriptions)
  // trackGoogleAdsConversion('YOUR_SUBSCRIBE_LABEL', { value, currency });
};

/**
 * Track Add to Wishlist
 * Called when a user adds a listing to wishlist
 * @param {object} params - { listingId, title, value, currency }
 */
export const trackAddToWishlist = (params = {}) => {
  const { listingId, title, value, currency = 'USD' } = params;
  
  // Meta Pixel - AddToWishlist
  trackMetaEvent('AddToWishlist', {
    content_type: 'product',
    content_ids: [listingId],
    content_name: title,
    value: value,
    currency: currency
  });
  
  // Google Analytics
  trackGAEvent('add_to_wishlist', {
    currency: currency,
    value: value,
    items: [{
      item_id: listingId,
      item_name: title
    }]
  });
};

// ============================================
// CUSTOM EVENTS
// ============================================

/**
 * Track Custom Event
 * For any event not covered by standard events
 * @param {string} eventName - Custom event name
 * @param {object} params - Event parameters
 */
export const trackCustomEvent = (eventName, params = {}) => {
  // Meta Pixel - Custom Event
  trackMetaEvent(eventName, params);
  
  // Google Analytics
  trackGAEvent(eventName, params);
};

export default {
  trackPageView,
  trackCompleteRegistration,
  trackLogin,
  trackViewContent,
  trackSearch,
  trackInitiateCheckout,
  trackPurchase,
  trackContact,
  trackLead,
  trackSubscribe,
  trackAddToWishlist,
  trackCustomEvent,
  trackMetaEvent,
  trackGAEvent,
  trackGoogleAdsConversion
};
